import EventEmitter from "../event-emitter";
import FormField from "../form-field";
import TelInput from "../components/tel-input";
import Select from "../components/select";

export class PaymentFormatField extends FormField {
    constructor(props) {
        super(props);
    }

    _beforeInit() {
        this.loading = true;

        this._checkProps();
        this._initElements();

        this.options = Array.from(this.$radioButtons).map(($radio) => new PaymentFormatOption({
            $root: $radio.closest('.f-payment-format-radio'),
            subscriptions: [
                {
                    event: 'active',
                    callback: this._handleActive.bind(this)
                },
                {
                    event: 'valueChange',
                    callback: this._handleChange.bind(this)
                },
                {
                    event: 'validationStatusChange',
                    callback: this._handleValidationStatusChange.bind(this)
                }
            ]
        }));

        Promise.all(this.options.map((option) => option.init()))
            .then(() => {
                this.finishLoading();
            });
    }

    _initElements() {
        super._initElements();

        this.$radioButtons = this.$input.querySelectorAll('input[type="radio"]');

        if (!this.$radioButtons.length) {
            throw new Error('RadioField: Radio buttons not found!');
        }
    }

    _afterInit() {
        this._setFirstChecked();
    }

    _handleActive(option) {
        this.controller.validate(option.valid);
        this._setStatus(this.controller.valid ? 'valid' : 'neutral');
        this.options.forEach((opt) => opt.collapse());
        option.expand();
    }

    _handleChange(value) {
        this.controller.setValue(value, { validate: false });
    }

    _handleValidationStatusChange(valid) {
        this.controller.validate(valid);
        this._setStatus(this.controller.valid ? 'valid' : 'neutral');
    }

    _setFirstChecked() {
        if (!this.options.length && !this.options.some((option) => option.$radio.checked)) {
            return;
        }

        this.options[0].setChecked();
    }

    _refresh() {
        this._setFirstChecked();
    }

    _reinit($root) {
        this.$root = $root;

        this._beforeInit();
        this._afterInit();
    }

}

class PaymentFormatOption {
    constructor(props = {}) {
        this.props = props;

        this.$root = this.props.$root;
        this.$radio = null;
        this.$collapsible = null;
        this.$telInput = null;
        this.$paymentsCountSelect = null;

        this.collapsible = null;
        this.telInput = null;
        this.paymentsCountSelect = null;

        this.name = null;
        this.value = null;
        this.valid = false;

        this.ACTIVE_EVENT = 'active';
        this.VALUE_CHANGE_EVENT = 'valueChange';
        this.VALIDATION_STATUS_CHANGE_EVENT = 'validationStatusChange';

        this._eventEmitter = new EventEmitter(this.props.subscriptions || []);
    }

    init() {
        return new Promise((resolve) => {
            this._checkProps();
            this._initElements();
            this._initComponents(resolve);
            this._addSubscriptions();
        }).then(() => {
            this.name = this.$radio.value;

            if (this.$radio.checked) {
                this.setChecked();
            }
        });
    }

    setValue() {
        this.value = {
            type: this.$radio.value
        };

        if (this.telInput) {
            this.value.tel = this.telInput.getValue();
        }

        if (this.paymentsCountSelect) {
            this.value.payments = this.paymentsCountSelect.getValue();
        }

        if (!this.$radio.checked) {
            return;
        }

        this._validate();
        this._eventEmitter.dispatch(this.VALUE_CHANGE_EVENT, this.value);
    }

    setChecked() {
        this.$radio.checked = true;
        this.$radio.dispatchEvent(new Event('change'));
    }

    setUnchecked() {
        this.$radio.checked = false;
        this.$radio.dispatchEvent(new Event('change'));
    }

    _checkProps() {
        if (!this.$root) {
            throw new Error('PaymentFormatOption: Root element not found!');
        }
    }

    _initElements() {
        this.$radio = this.$root.querySelector('input[type="radio"]');
        this.$collapsible = this.$root.querySelector('[data-collapsible="collapsible"]');

        if (this.$collapsible) {
            this.$telInput = this.$root.querySelector('[data-field="tel"]');
            this.$paymentsCountSelect = this.$root.querySelector('[data-field="payments"]');
        }
    }

    _initComponents(done) {
        this._initCollapsible();
        this._initPaymentsCountSelect();
        this._initTelInput(done);
    }

    _addSubscriptions() {
        this.$root.addEventListener('click', this._handleOptionClick.bind(this));
        this.$radio.addEventListener('change', this._handleActive.bind(this));
    }

    _initCollapsible() {
        if (!this.$collapsible) {
            return;
        }

        this.collapsible = new PaymentFormatRadioCollapsible({
            $root: this.$collapsible
        });
    }

    _initTelInput(done) {
        if (!this.$telInput) {
            done();
            return;
        }

        this.telInput = new TelInput({
            $root: this.$telInput,
            subscriptions: [
                {
                    event: 'afterInit',
                    callback: done
                },
                {
                    event: 'valueChange',
                    callback: this._handleTelInputValueChange.bind(this)
                },
                {
                    event: 'blur',
                    callback: this._handleTelInputBlur.bind(this)
                }
            ]
        });
    }

    _initPaymentsCountSelect() {
        if (!this.$paymentsCountSelect) {
            return;
        }

        this.paymentsCountSelect = new Select({
            $root: this.$paymentsCountSelect,
            subscriptions: [
                {
                    event: 'valueChange',
                    callback: this.setValue.bind(this)
                }
            ]
        });
        this.paymentsCountSelect.selectLastOption();
    }

    _handleActive(e) {
        this.setValue();
        this._eventEmitter.dispatch(this.ACTIVE_EVENT, this);
    }

    _handleOptionClick(e) {
        if (this.$radio.checked) {
            return;
        }

        this.$radio.checked = true;
        this.$radio.dispatchEvent(new Event('change'));
    }

    _validate() {
        this.valid = true;

        if (this.telInput && !this.telInput.valid) {
            this.valid = false;
        }

        if (this.paymentsCountSelect && !this.paymentsCountSelect.getValue()) {
            this.valid = false;
        }

        this._eventEmitter.dispatch(this.VALIDATION_STATUS_CHANGE_EVENT, this.valid);
    }

    _handleTelInputValueChange(value) {
        this.$telInput.classList.remove('-invalid');
        this.setValue();
    }

    _handleTelInputBlur() {
        if (this.telInput.valid) {
            return;
        }

        this.$telInput.classList.add('-invalid');
    }

    expand() {
        if (this.collapsible) {
            this.collapsible.expand();
        }
    }

    collapse() {
        if (this.collapsible) {
            this.collapsible.collapse();
        }
    }
}

export default class PaymentFormatRadioCollapsible {
    constructor(props = {}) {
        this.props = props;
        this.$root = this.props.$root;
        this.$body = null;

        this.init();
    }

    init() {
        if (!this.$root) {
            throw new Error('Collapsible: Root element not found!');
        }

        this.$body = this.$root.querySelector('[data-collapsible="body"]');

        if (!this.$body) {
            throw new Error('Collapsible: Body element not found!');
        }
    }

    expand() {
        this.$body.style.display = 'flex';
        const bodyHeight = this.$body.offsetHeight;
        this.$body.removeAttribute('style');
        this.$root.style.height = `${bodyHeight}px`;
        this.$root.classList.add('-expanded');
    }

    collapse() {
        this.$root.removeAttribute('style');
        this.$root.classList.remove('-expanded');
    }
}